import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, InputNumber, Radio } from 'antd';
import _ from 'lodash';

import { updateFilterItemInList } from '../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../hooks/useActions';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';

/** ID фильтров с минимальным значением 1 */
const MIN_VALUE_ONE_IDS = [
    'applications_payments_orders_days_end',
    'applications_payments_reg_days_end',
    'webinar_reg_days_end',
    'applications_users_days_end'
];

/** Интерфейс данных фильтра */
interface FilterData {
    number: string | null;
    [key: string]: unknown;
}

/** Props для NumberPicker */
interface NumberPickerProps {
    isModal?: boolean;
    isLocalMode?: boolean;
    localFilters?: any[];
    setLocalFilters?: (filters: any[]) => void;
    disabled?: boolean;
    id: string;
    index?: number;
    modalVisibleHandler: (visible: boolean) => void;
    itemData?: FilterData;
    list?: boolean;
    [key: string]: unknown;
}

const NumberPicker: React.FC<NumberPickerProps> = ({
    isModal = false,
    isLocalMode = false,
    localFilters,
    setLocalFilters,
    disabled = false,
    id,
    index,
    modalVisibleHandler,
    itemData,
    list,
    ...props
}) => {
    const { filters: globalFilters } = useTypedSelector((state) => 
        isModal ? state.filter.dashboardModal : state.filter
    );
    const filters = isLocalMode ? localFilters || [] : globalFilters;
    const { setFilters, setFilter, setMetaFilterUpdate } = useActions();

    const [filterMode, setFilterMode] = useState(1);

    // Приводим id к строке для безопасной работы с методами строк
    const safeId = String(id ?? '');

    // Вычисляем ID с учётом режима include/exclude
    const filterId = useMemo(() => {
        if (filterMode === 2) {
            return safeId.includes('not_') ? safeId : `not_${safeId}`;
        }
        return safeId.includes('not_') ? safeId.split('not_')[1] : safeId;
    }, [safeId, filterMode]);

    // Инициализация режима из id
    useEffect(() => {
        setFilterMode(safeId.includes('not_') ? 2 : 1);
    }, [safeId]);

    // Определяем режим редактирования
    const isEditing = useMemo(() => {
        return Boolean(list && itemData);
    }, [list, itemData]);

    // Мемоизированные начальные данные фильтра
    const initialFilter = useMemo((): FilterData => {
        if (itemData && list) {
            return itemData;
        }
        return { ...props, number: null };
    }, [itemData, list, props]);

    const [currentData, setCurrentData] = useState<FilterData>(initialFilter);

    // Синхронизация currentData при изменении itemData
    useEffect(() => {
        if (itemData && list) {
            setCurrentData(itemData);
        }
    }, [itemData, list]);

    const onApply = useCallback(() => {
        const _currentData = {
            ...currentData,
            id: filterId?.toString()
        };

        if (isLocalMode && setLocalFilters) {
            // Локальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setLocalFilters(_filters);
            } else {
                setLocalFilters([...filters, _currentData]);
            }
        } else {
            // Глобальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setFilters(isModal ? { isModal, data: _filters } : _filters);
            } else {
                setFilter({ ..._currentData, isModal });
            }
            setMetaFilterUpdate({ value: true });
        }

        modalVisibleHandler(false);
    }, [currentData, filterId, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, setFilters, setFilter, setMetaFilterUpdate, modalVisibleHandler]);

    const onChange = useCallback((val: number | null) => {
        let _val = val;

        // Для определённых фильтров минимальное значение = 1
        if (MIN_VALUE_ONE_IDS.includes(id)) {
            if (typeof _val !== 'number' || _val < 1) {
                _val = 1;
            }
        }

        setCurrentData((prevState) => ({
            ...prevState,
            number: _val?.toString() ?? null
        }));
    }, [id]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !disabled) {
            e.preventDefault();
            onApply();
        }
    }, [disabled, onApply]);

    return (
        <div onKeyDown={handleKeyDown}>
            <div className="picker-radio-group">
                <Radio.Group disabled={disabled} onChange={(e) => setFilterMode(e.target.value)} value={filterMode}>
                    <Radio value={1}>Включить</Radio>
                    <Radio value={2}>Исключить</Radio>
                </Radio.Group>
            </div>

            <InputNumber<string>
                stringMode 
                disabled={disabled} 
                value={currentData.number ?? undefined} 
                onChange={onChange as any} 
                min="0"
                style={{ width: 200 }} 
            />
            <br />
            <Button disabled={disabled} type="primary" className="picker-apply-btn" onClick={onApply}>
                Применить
            </Button>
        </div>
    );
};

export default NumberPicker;
